/**
 * ChannelPicker - Grid de seleção de canal para criação de inbox
 *
 * Reaproveita as regras visuais centralizadas em `channelVisuals.ts`
 * (mesmos ícones/cores/gradientes usados nos cards da listagem de inboxes).
 *
 * RELEASE:
 *   2026-08-08 v3.0.0 - Visual premium: barra de acento em gradiente, bloom,
 *                       tile de ícone colorido e elevação no hover.
 *   2026-08-08 v2.1.0 - Descrição do WhatsApp Lite sem citar provedores não oficiais.
 *   2026-08-08 v2.0.0 - Cartões maiores com tile de ícone, descrição e badge de seleção.
 *   2026-08-08 v1.0.0 - Extraído do CreateInboxDialog (fim do wizard).
 */
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { getChannelVisual } from '@/components/tenant/inbox/channelVisuals';
import type { ChannelType } from '@/components/tenant/InboxCard';

// Ordem de exibição dos canais no grid
export const CHANNEL_ORDER: ChannelType[] = [
  'whatsapp',
  'whatsapp_oficial',
  'instagram',
  'messenger',
  'telegram',
  'webchat',
];

/**
 * Rótulos customizados exibidos apenas neste seletor de canais.
 * Sobrescreve o label padrão de `channelVisuals` sem afetar outras telas.
 */
const CHANNEL_LABEL_OVERRIDES: Partial<Record<ChannelType, string>> = {
  whatsapp: 'WhatsApp Lite',
};

/** Descrição curta de cada canal (uma linha de apoio no cartão) */
const CHANNEL_DESCRIPTIONS: Record<ChannelType, string> = {
  whatsapp: 'Conecte um número comum de WhatsApp em poucos minutos.',
  whatsapp_oficial: 'Conecte sua conta oficial do WhatsApp Business.',
  instagram: 'Conecte o Direct do Instagram e responda em um só lugar.',
  messenger: 'Conecte sua página do Facebook e converse via Inbox.',
  telegram: 'Conecte seus bots ou usuários do Telegram.',
  webchat: 'Adicione um chat ao seu site e atenda em tempo real.',
};

interface ChannelPickerProps {
  /** Canal atualmente selecionado */
  value: ChannelType | null;
  /** Callback disparado ao escolher um canal */
  onChange: (channel: ChannelType) => void;
  /** Desabilita a troca de canal (ex.: durante o salvamento) */
  disabled?: boolean;
}

/**
 * Grid de canais — clique único, sem etapas.
 */
export function ChannelPicker({ value, onChange, disabled }: ChannelPickerProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
      {CHANNEL_ORDER.map((channel) => {
        const { Icon, label: defaultLabel, accentGradient, bloomBg } = getChannelVisual(channel);
        const label = CHANNEL_LABEL_OVERRIDES[channel] ?? defaultLabel;
        const isSelected = value === channel;

        return (
          <button
            key={channel}
            type="button"
            disabled={disabled}
            onClick={() => onChange(channel)}
            aria-pressed={isSelected}
            className={cn(
              'group relative overflow-hidden rounded-2xl border bg-card p-4 pt-5 text-left',
              'transition-all duration-200 will-change-transform',
              'hover:-translate-y-0.5 hover:shadow-lg hover:border-transparent',
              'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none',
              isSelected
                ? 'border-transparent ring-2 ring-primary shadow-md'
                : 'border-border',
            )}
          >
            {/* Barra de acento no topo (gradiente da cor do canal) */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute inset-x-0 top-0 h-1 bg-gradient-to-r transition-opacity duration-200',
                accentGradient,
                isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-100',
              )}
            />

            {/* Bloom decorativo no canto */}
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl transition-opacity duration-200',
                bloomBg,
                isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
            />

            {/* Badge de seleção */}
            {isSelected && (
              <span className="absolute right-3 top-4 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}

            <div className="relative flex flex-col items-start gap-3">
              {/* Tile do ícone do canal em gradiente */}
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br shadow-sm',
                  'transition-transform duration-200 group-hover:scale-105',
                  accentGradient,
                )}
              >
                <Icon className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>

              <div className="space-y-1">
                <span className="block text-sm font-semibold text-foreground leading-tight">
                  {label}
                </span>
                <span className="block text-xs text-muted-foreground leading-snug">
                  {CHANNEL_DESCRIPTIONS[channel]}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
